<template>
  <div>
    <v-row dense class="mb-2">
      <v-col cols="12" sm="8">
        <BTextField
          v-model="form.name"
          validate="required"
          :disabled="disabled"
          label="Название"
          name="name"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="6" sm="auto">
        <BTextFieldDate
          v-model="form.start_at"
          validate="required"
          :min="publication.start_at"
          :max="publication.end_at || form.end_at"
          :disabled="disabled"
          label="Дата начала размещения"
          data-vv-as="Дата начала"
          placeholder="Введите дату"
          name="start_at"
          datetime
        />
      </v-col>

      <v-col cols="6" sm="auto">
        <BTextFieldDate
          v-model="form.end_at"
          :disabled="disabled"
          :min="publication.start_at || form.start_at"
          :max="publication.end_at"
          label="Дата окончания размещения"
          data-vv-as="Дата окончания"
          placeholder="Введите дату"
          name="end_at"
          datetime
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" :md="6">
        <v-checkbox
          v-model="form.is_use_timezones"
          :disabled="disabled"
          label="Использовать таймзону пользователя?"
          dense
          hide-details
          class="ma-0 pa-0"
          name="is_use_timezones"
        />
      </v-col>

      <v-col cols="12" :md="6">
        <v-checkbox
          v-model="form.is_delete_storage_after_publication"
          :disabled="disabled"
          label="Удалить хранилище после закрытия публикации"
          dense
          hide-details
          class="ma-0 pa-0"
          name="is_delete_storage_after_publication"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12" md="auto">
        <BAutocompleteCommon
          v-model="form.storage_type"
          validate="required"
          :disabled="disabled"
          $type="storage-type"
          name="storage_type"
        />
      </v-col>
    </v-row>

    <template v-if="hasParticipants">
      <v-divider class="mt-4" />

      <v-row dense class="my-2">
        <v-col>
          <BTableWithAutocompleteAsync
            :value="form.participants"
            :actions="actions"
            :readonly="disabled"
            :loading="isLoading"
            type-table="participants"
            type-autocomplete="participants"
            name="participants"
            return-object
            @action:append="onAppend"
            @action:delete="onRemove"
            @action:edit="onEditParticipant"
          >
            <template #header>
              <h3 class="mb-2">
                Участники
              </h3>
            </template>
          </BTableWithAutocompleteAsync>
        </v-col>
      </v-row>

      <v-divider class="mt-2" />
    </template>
  </div>
</template>

<script>
import { StorageTypes } from '@/typings/api/Publication'

import BTextField from '@/components/forms/BTextField.vue'
import BAutocompleteCommon from '@/components/forms/BAutocompleteCommon.vue'
import BTextFieldDate from '@/components/forms/BTextFieldDate.vue'
import BTableWithAutocompleteAsync from '@/components/common/BTable/BTableWithAutocompleteAsync.vue'
import { DELETE, EDIT } from '@/helpers/enums/table-actions'
import { createPublicationsParticipantModalModal } from '@/views/Publications/utils'
import { unionBy } from 'lodash'
import { PublicationsStorageApi } from '@/api/resources'
import { Adapter } from '@/store/createCrudModule'

export const TABLE_ACTIONS = Object.freeze([
  EDIT,
  DELETE,
])


export const createDefaultData = (start_at, end_at) => ({
  // id
  name: '',
  storage_type: StorageTypes.general,
  start_at,
  end_at,
  is_use_timezones: false,
  is_delete_storage_after_publication: false,
  participants: [],
  // created_at
  // updated_at
})

export default {
  name: 'PublicationStorageForm',
  components: {
    BTextField,
    BAutocompleteCommon,
    BTextFieldDate,
    BTableWithAutocompleteAsync,
  },
  props: {
    disabled: Boolean,
    form: {
      type: Object,
      required: true,
    },
    publication: {
      type: Object,
      required: true,
      validator: (prop) => (
        prop
        && 'start_at' in prop
        && 'end_at' in prop
      ),
    },
  },
  data: () => ({
    isLoading: false,
    actions: TABLE_ACTIONS,
  }),
  computed: {
    hasParticipants() {
      return !!this.form.id && this.form.storage_type === StorageTypes.general
    },
  },
  created() {
    const api = new PublicationsStorageApi()
    this.adapter = new Adapter(api)
  },
  methods: {
    async onAppend(data) {
      this.isLoading = true
      try {
        await this.adapter.api.addParticipants(
          this.form.id,
          { publication_id: this.publication.id, participants_id: data.id },
        )

        this.form.participants = unionBy(this.form.participants, [data], 'id')
      } catch (e) {
        this.$notice.errors(e)
      }
      this.isLoading = false
    },
    async onRemove(data, callback) {
      const unwatch = callback?.()
      this.isLoading = true
      try {
        await this.adapter.api.removeParticipants(
          this.form.id,
          { publication_id: this.publication.id, participants_id: data.id },
        )

        const value = this.form.participants.slice()
        const index = value.findIndex(({ id }) => id === data.id)
        if (index >= 0) {
          value.splice(index, 1)
          this.form.participants = value
        }
      } catch (e) {
        this.$notice.errors(e)
      } finally {
        this.isLoading = false
        unwatch?.()
      }
    },
    async getParticipants() {
      this.isLoading = true
      try {
        const { data } = await this.adapter.api.getOne(
          this.form.id,
          { publication_id: this.publication.id },
        )
        this.form.participants = [].concat(data.participants)
      } catch (e) {
        this.$notice.errors(e)
      }
      this.isLoading = false
    },
    async onEditParticipant(participant) {
      const propsData = { participant_id: participant.id }
      const modal = await createPublicationsParticipantModalModal(this, propsData)
      modal.$on('closed', this.getParticipants)
      await modal.open()
    },
  },
}
</script>
